import styled from 'styled-components';

import { Button } from '@/components/UI/Button';
import { H3 } from '@/components/UI/Typography/Heading';
import { Text } from '@/components/UI/Typography/Text';

export const MeetingPageShell = styled.div`
  width: 100%;
  min-width: 0;
`;

export const CaseDeadlineNotice = styled.div`
  margin-top: 18px;
  padding: 18px 22px;
  border-left: 4px solid #b65d16;
  background: #fff4e8;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

export const SummaryPanel = styled.section<{ isTablet: boolean }>`
  box-sizing: border-box;
  padding: ${(p) => (p.isTablet ? '30px 18px' : '30px 52px')};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 639px) {
    padding: 20px 12px 24px;
    border-radius: 12px;
  }
`;

export const CalendarPanel = styled.section<{ isTablet: boolean }>`
  box-sizing: border-box;
  margin-top: 18px;
  padding: ${(p) => (p.isTablet ? '26px 18px' : '30px 52px')};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  .pbl-lawyer-calendar {
    min-width: 680px;
  }

  .fc {
    color: ${(p) => p.theme.colors.gray500};
    font-family: inherit;
  }

  .fc .fc-toolbar.fc-header-toolbar {
    margin-bottom: 18px;
  }

  .fc .fc-toolbar-title {
    color: ${(p) => p.theme.colors.gray500};
    font-size: ${(p) => p.theme.fontSizes.md};
    font-weight: 400;
  }

  .fc .fc-toolbar-chunk {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .fc button.fc-button-primary {
    border-color: ${(p) => p.theme.colors.gray100};
    border-radius: ${(p) => p.theme.radii.base};
    background-color: ${(p) => p.theme.colors.gray100};
    color: ${(p) => p.theme.colors.gray500};
    text-transform: none;
    box-shadow: none;
  }

  .fc button.fc-button-primary:not(:disabled):active,
  .fc button.fc-button-primary:not(:disabled).fc-button-active {
    border-color: ${(p) => p.theme.colors.primary100};
    background-color: ${(p) => p.theme.colors.light};
    color: ${(p) => p.theme.colors.gray500};
    box-shadow: none;
  }

  .fc .fc-col-header-cell {
    height: 38px;
    vertical-align: middle;
  }

  .fc .fc-col-header-cell-cushion,
  .fc .fc-daygrid-day-number {
    color: ${(p) => p.theme.colors.gray500};
    font-weight: 400;
    text-decoration: none;
  }

  .fc .pbl-calendar-day-button {
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }

  .fc .pbl-calendar-day-button:disabled {
    color: ${(p) => p.theme.colors.gray200};
    cursor: not-allowed;
  }

  .fc .fc-daygrid-day.pbl-calendar-disabled-day {
    background: ${(p) => p.theme.colors.gray000};
  }

  .fc .fc-daygrid-day-top {
    justify-content: center;
  }

  .fc .fc-daygrid-day-number {
    padding-top: 10px;
  }

  .fc .fc-day-other .fc-daygrid-day-number {
    color: ${(p) => p.theme.colors.gray200};
  }

  .fc .fc-day-other .pbl-calendar-day-button {
    color: ${(p) => p.theme.colors.gray200};
  }

  .fc .fc-daygrid-day.fc-day-today {
    background: transparent;
  }

  .fc .fc-daygrid-day.pbl-selected-day {
    background: #c7def9;
  }

  .fc-theme-standard td,
  .fc-theme-standard th {
    border-color: ${(p) => p.theme.colors.gray100};
  }

  .fc-theme-standard .fc-scrollgrid {
    border-color: ${(p) => p.theme.colors.gray100};
    border-radius: ${(p) => p.theme.radii.lg};
    overflow: hidden;
  }

  .fc .fc-scrollgrid,
  .fc .fc-scrollgrid table,
  .fc .fc-col-header,
  .fc .fc-daygrid-body,
  .fc .fc-daygrid-body table,
  .fc .fc-scrollgrid-sync-table {
    width: 100% !important;
    table-layout: fixed;
  }

  .fc .fc-daygrid-event {
    border: 0;
    border-radius: ${(p) => p.theme.radii.base};
    padding: 2px 6px;
    background: ${(p) => p.theme.colors.primary500};
    color: ${(p) => p.theme.colors.light};
  }

  @media (max-width: 900px) {
    overflow-x: auto;

    .fc .fc-toolbar.fc-header-toolbar {
      align-items: flex-start;
      flex-direction: column;
      gap: 12px;
    }
  }

  @media (max-width: 639px) {
    padding: 20px 12px 24px;
    border-radius: 12px;
    overflow: visible;

    .pbl-lawyer-calendar {
      min-width: 0;
    }

    .fc .fc-toolbar.fc-header-toolbar {
      align-items: center;
      flex-direction: row;
      gap: 8px;
      margin-bottom: 14px;
    }

    .fc .fc-toolbar-title {
      font-size: 16px;
      font-weight: 700;
    }

    .fc .fc-toolbar-chunk {
      min-width: 0;
      gap: 4px;
    }

    .fc button.fc-button-primary {
      min-width: 30px;
      min-height: 30px;
      padding: 4px 7px;
      font-size: 12px;
    }

    .fc .fc-col-header-cell-cushion,
    .fc .fc-daygrid-day-number {
      font-size: 11px;
    }

    .fc .fc-daygrid-day-number {
      padding: 6px 2px;
    }

    .fc .fc-daygrid-day-frame {
      min-height: 48px;
    }

    .fc .fc-daygrid-event {
      max-width: 100%;
      padding: 1px 3px;
      font-size: 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

export const CalendarScroller = styled.div`
  overflow-x: auto;

  @media (max-width: 639px) {
    overflow: visible;
  }
`;

export const MeetingModalSurface = styled.form`
  box-sizing: border-box;
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(650px, calc(100vw - 32px));
  padding: 34px 24px 20px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};
  transform: translate(-50%, -50%);
  box-shadow: none;

  @media (max-width: 639px) {
    width: calc(100vw - 30px);
    max-height: calc(100vh - 30px);
    padding: 34px 16px 20px;
    overflow-y: auto;
  }
`;

export const MeetingModalCloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.md};
  line-height: 1;
  cursor: pointer;
`;

export const MeetingModalTitle = styled(H3)`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
`;

export const MeetingFieldStack = styled.div`
  display: grid;
  gap: 34px;
`;

export const MeetingOptionPanel = styled.div`
  display: grid;
  gap: 16px;
`;

export const MeetingOptionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
`;

export const MeetingOptionActionRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

export const MeetingSecondaryButton = styled.button`
  min-height: 30px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.sm};
  cursor: pointer;
`;

export const MeetingFieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const MeetingInputWrap = styled.label`
  position: relative;
  display: block;
`;

export const MeetingInput = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 0 8px;
  border: 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
  background: transparent;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  outline: none;

  &[aria-invalid='true'] {
    border-bottom-color: ${(p) => p.theme.colors.error};
  }
`;

export const MeetingEndOutput = styled.output`
  display: flex;
  align-items: center;
  width: 100%;
  height: 38px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
`;

export const MeetingDescriptionInput = styled(MeetingInput)`
  padding-right: 0;
`;

export const MeetingModalAction = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 42px;

  @media (max-width: 520px) {
    flex-direction: column;
  }
`;

export const MeetingActionButton = styled(Button)`
  min-width: 158px;
`;

export const MeetingSuccessTitle = styled(H3)`
  margin: 0 0 12px;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.lg};
  text-align: center;
`;

export const MeetingSuccessText = styled(Text)`
  color: ${(p) => p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.base};
  text-align: center;
`;

export const HiddenLabelText = styled(Text)`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
`;

export const MeetingValidationText = styled(Text)`
  display: block;
  margin-top: 4px;
  color: ${(p) => p.theme.colors.error};
  font-size: ${(p) => p.theme.fontSizes.sm};
  line-height: 1.2;
`;
